import React, { useEffect, useRef, useState } from "react";
import styles from "../styles/Chatbot.module.css";
import { GoPaperclip } from "react-icons/go";
import { IoIosPaperPlane } from "react-icons/io";
import Typed from "typed.js";
import { FiCopy } from "react-icons/fi"; // React Icons
import { IoCheckmark } from "react-icons/io5";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CodeBlock, github } from "react-code-blocks";
import { marked } from "marked"; // Use marked library to parse markdown to HTML
import { Box, Typography, Tooltip } from "@mui/material";
import ChatMarkdown from "../components/ChatMarkdown";

const ChatList = ({
  scrollToBottom,
  currentReply,
  setCurrentReply,
  index,
  length,
  loader,
  setLoader,
  message,
  reply,
  replyed,
}) => {
  const [copied, setCopied] = useState(false);
  const [copy_code, setCopy_code] = useState(false);
  const [typing, setTyping] = useState(false);

  const handleCopy = (code) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const handleCodeCopy = (code) => {
      if (!code) return; // Prevent copying empty code

    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopy_code(true);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
    setTimeout(() => {
      setCopy_code(false);
    }, 2000);
  };

  const typedElementRef = useRef(null);

  useEffect(() => {
    if (!loader) {
      if (index + 1 === length && currentReply) {
        // Ensure last message + a reply

        try {
          setTyping(true);
          const parsedHtml = marked(replyed, { gfm: true });
          scrollToBottom();

          setTimeout(() => {
            if (typedElementRef.current) {
             
              const options = {
                strings: [parsedHtml], // Typing effect applies to this text
                typeSpeed: 1,
                backSpeed: 25,
                showCursor: false,
                contentType: "html", // Important: Preserve HTML tags (e.g., <br> for new lines)
                onComplete: () => {
                  setTyping(false);
                  setCurrentReply(false);
                  scrollToBottom(); // Ensure it scrolls to the very bottom after typing completes
                },
              };

              const typedInstance = new Typed(typedElementRef.current, options);

              return () => {
                try {
                  typedInstance.destroy(); // Cleanup on unmount
                } catch (cleanupError) {
                  console.error(
                    "Error destroying Typed.js instance:",
                    cleanupError
                  );
                }
              };
            } else {
              console.warn("typedElementRef is null, skipping Typed.js");
              setTyping(false);
            }
          }, 100);
        } catch (error) {
          console.error("Error in typing effect:", error);
          setTyping(false);
          setCurrentReply(false);
        }
      }
    }
  }, [replyed, setTyping, currentReply, loader, index, length]);

  const replyLoader = () => {
    return (
      <div class={styles.loader_container}>
        <div class={styles.dot}></div>
        <div class={styles.dot}></div>
        <div class={styles.dot}></div>
      </div>
    );
  };
  console.log("Message IS",message);
  
  // Scroll whenever `typing` changes
  useEffect(() => {
    let interval;
    if (typing) {
        interval = setInterval(() => {
            scrollToBottom()
          },1000)
    }
    return () => clearInterval(interval);
  }, [typing]);
  
  return (
    <>
      {message ? (
        <div className={styles.message}>
          <div className={styles.message_content}>
            <div className={styles.text_content}>
              {message.startsWith("[Image](") ? (
                <img
                  src={message.match(/\(([^)]+)\)/)[1]}
                  alt="Chat Image"
                  className={styles.chat_image}
                />
              ) : (
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ href, children }) => {
                        if (href.startsWith("http")) {
                          const fileName = children[0]; // Extract file name from Markdown link
                          return (
                            <div className={styles.filePreview}>
                              <GoPaperclip className={styles.fileIcon} />
                              <a href={href} target="_blank" rel="noopener noreferrer">
                                {fileName}
                              </a>
                            </div>
                          );
                        }
                        return <a href={href}>{children}</a>;
                      },
                    }}
                  >{message}</Markdown>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {
        replyed ?(
          <div className={styles.reply}>
            <div
              className={`d-flex w-100 ${loader && index + 1 === length
                ? "align-items-center"
                : "align-items-start"
                }`}
            >
              <img
                src="/images/sparkles.svg"
                className="img-fluid"
                alt="Ackroll Ai chat bot"
              />
              {loader && index + 1 === length ? (
                replyLoader()
              ) : (
                <div className={styles.reply_content}>
                  <div className={styles.text_content}>
                    {typing ? (
                      <div ref={typedElementRef}>
                        <Markdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            code({ inline, className, children, ...props }) {
                              const match = /language-(\w+)/.exec(className || "");
                              const codeText = String(children).replace(/\n$/, "");

                              return !inline && match ? (
                                <Box sx={{ position: "relative", my: 2 }}>
                                  <div className={styles.code_header}>
                                    <Typography variant="body2">
                                      {match[1].toUpperCase()}
                                    </Typography>
                                    <button
                                      className={styles.copy}
                                      onMouseDown={(e) => {
                                        e.stopPropagation(); // Prevent unnecessary bubbling
                                        handleCodeCopy(codeText);
                                        console.log("clicked");
                                      }}
                                    >
                                      <FiCopy />
                                      {copy_code ? (
                                        <span>Copied!</span>
                                      ) : (
                                        <span>Copy</span>
                                      )}
                                    </button>
                                  </div>

                                  {/* Code Block */}
                                  <CodeBlock
                                    text={codeText}
                                    language={match[1]}
                                    showLineNumbers
                                    theme={github}
                                    wrapLines
                                    codeBlock
                                  />
                                </Box>
                              ) : (
                                <code
                                  className={className}
                                  {...props}
                                  style={{
                                    backgroundColor: "#f5f5f5",
                                    padding: "2px 5px",
                                    borderRadius: "3px",
                                  }}
                                >
                                  {children}
                                </code>
                              );
                            },
                          }}
                        ></Markdown>
                      </div>
                    ) : (
                      <ChatMarkdown replyed={replyed} />
                    )}
                  </div>
                  <div className={styles.reply_buttons}>
                    <button
                      type={"button"}
                      onClick={() => {
                        handleCopy(replyed);
                      }}
                      className={styles.copy_text}
                    >
                      {copied ? <IoCheckmark /> : <FiCopy />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) :  loader && index + 1 === length && (
          replyLoader()
        ) 
      }
    
    </>
  );
};

export default ChatList;

